import { useCallback, useEffect, useState } from 'react';
import { useVault } from '../lib/vault';
import { api } from '../lib/api';
import { formatMoney } from '@shared/money';
import { plural } from '@shared/text';
import { ACTION_HINT, ACTION_LABEL, type OffboardingEntry, type OffboardingPlan } from '@shared/types';
import { Confirm, ErrorNote, useAction } from '../components/ui';

function PlanCard({
  plan,
  onChange,
}: {
  plan: OffboardingPlan;
  onChange: (plan: OffboardingPlan) => void;
}): JSX.Element {
  const { busy, error, run } = useAction();
  const done = plan.entries.filter((e) => e.done).length;
  const total = plan.entries.length;
  const progress = total === 0 ? 100 : Math.round((done / total) * 100);
  const openEntries = plan.entries.filter((e) => !e.done);
  const ownerEntries = plan.entries.filter((e) => e.wasOwner);
  const key = (e: OffboardingEntry): string => `${e.itemId}~${e.assignmentId ?? ''}`;

  return (
    <div className="card">
      <div className="card-title">
        <div>
          <h2>{plan.name}</h2>
          <p className="small muted" style={{ margin: '3px 0 0' }}>
            {plan.email} · gestartet am {new Date(plan.startedAt).toLocaleDateString('de-DE')}
          </p>
        </div>
        {plan.completedAt ? (
          <span className="badge badge-ok">
            abgeschlossen am {new Date(plan.completedAt).toLocaleDateString('de-DE')}
          </span>
        ) : (
          <span className={progress === 100 ? 'badge badge-ok' : 'badge badge-warn'}>
            {done} von {total} erledigt
          </span>
        )}
      </div>

      <ErrorNote error={error} />

      <div className="bar-track" style={{ marginBottom: 14 }}>
        <div
          className="bar-fill"
          style={{ width: `${progress}%`, background: progress === 100 ? 'var(--ok)' : undefined }}
        />
      </div>

      {!plan.completedAt && openEntries.length > 0 ? (
        <div className="note note-warn">
          Der Zugriff in LOCKER ist entzogen, aber beim Anbieter{' '}
          {plural(openEntries.length, 'ist noch ein Punkt', 'sind noch Punkte')} offen. Solange sich
          daran nichts ändert, kommt die Person dort weiter hinein – sie hat sich die Zugangsdaten
          womöglich notiert.
        </div>
      ) : null}

      {ownerEntries.length > 0 ? (
        <div className="note" style={{ marginTop: 10 }}>
          <strong>{plural(ownerEntries.length, 'Vertrag war', 'Verträge waren')}</strong> dieser
          Person zugeordnet. Trag im jeweiligen Eintrag eine neue verantwortliche Person ein.
        </div>
      ) : null}

      <table style={{ marginTop: 14 }}>
        <thead>
          <tr>
            <th style={{ width: 34 }} />
            <th>Was zu tun ist</th>
            <th>Wo</th>
            <th className="num">pro Monat</th>
          </tr>
        </thead>
        <tbody>
          {plan.entries.map((entry) => (
            <tr key={key(entry)}>
              <td>
                <input
                  type="checkbox"
                  checked={entry.done}
                  disabled={busy || Boolean(plan.completedAt)}
                  aria-label={`${entry.title} erledigt`}
                  style={{ width: 'auto' }}
                  onChange={async (e) => {
                    const next = await run(() =>
                      api.checkOffboardingEntry(
                        plan.uid,
                        entry.itemId,
                        entry.assignmentId,
                        e.target.checked,
                      ),
                    );
                    if (next) onChange(next);
                  }}
                />
              </td>
              <td>
                <div className="row-tight">
                  <strong style={{ textDecoration: entry.done ? 'line-through' : undefined }}>
                    {ACTION_LABEL[entry.action]}
                  </strong>
                  {entry.criticality === 'high' ? (
                    <span className="badge badge-danger">zuerst</span>
                  ) : null}
                  {entry.wasOwner ? <span className="badge">Vertrag übergeben</span> : null}
                </div>
                <div className="small muted">{ACTION_HINT[entry.action]}</div>
              </td>
              <td>
                <div className="small">
                  <strong>{entry.title}</strong>
                  {entry.label ? <span className="muted"> · {entry.label}</span> : null}
                </div>
                {entry.username ? (
                  <div className="small mono muted">{entry.username}</div>
                ) : entry.url ? (
                  <a className="small" href={entry.url} target="_blank" rel="noreferrer noopener">
                    {entry.url.replace(/^https?:\/\//, '')}{'\u00a0↗'}
                  </a>
                ) : null}
              </td>
              <td className="num mono small">
                {entry.monthlyCents ? formatMoney(entry.monthlyCents) : '–'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {!plan.completedAt ? (
        <div className="row" style={{ marginTop: 14 }}>
          <span className="spacer" />
          <button
            className="btn-primary"
            disabled={busy || openEntries.length > 0}
            title={openEntries.length > 0 ? 'Erst alle Punkte abhaken' : undefined}
            onClick={async () => {
              const next = await run(() => api.completeOffboarding(plan.uid));
              if (next) onChange(next);
            }}
          >
            Offboarding abschließen
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * `startFor` kommt von der Mannschaftsliste: dort steht die Person schon fest,
 * also soll man sie hier nicht noch einmal aus einer Liste suchen muessen.
 */
export function OffboardingPage({ startFor = null }: { startFor?: string | null } = {}): JSX.Element {
  const { users, me, reload } = useVault();
  const [plans, setPlans] = useState<OffboardingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [startUid, setStartUid] = useState(startFor ?? '');
  const [confirmStart, setConfirmStart] = useState(false);

  useEffect(() => {
    if (startFor) setStartUid(startFor);
  }, [startFor]);
  const { busy, error, setError, run } = useAction();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPlans(await api.offboardingPlans());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [setError]);

  useEffect(() => {
    void load();
  }, [load]);

  const candidates = users.filter((u) => u.uid !== me?.uid && u.status === 'active');
  const target = users.find((u) => u.uid === startUid);
  const open = plans.filter((p) => !p.completedAt);
  const closed = plans.filter((p) => p.completedAt);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Offboarding</h1>
        </div>
      </div>

      <ErrorNote error={error} />

      <div className="card">
        <div className="card-title">
          <h2>Austritt starten</h2>
        </div>
        <div className="row">
          <select
            value={startUid}
            onChange={(e) => setStartUid(e.target.value)}
            style={{ maxWidth: 320 }}
          >
            <option value="">Person auswählen …</option>
            {candidates.map((u) => (
              <option key={u.uid} value={u.uid}>
                {u.name} ({u.email})
              </option>
            ))}
          </select>
          <button
            className="btn-danger"
            disabled={!startUid || busy}
            onClick={() => setConfirmStart(true)}
          >
            Offboarding starten
          </button>
        </div>
      </div>

      {loading ? (
        <div className="empty">Wird geladen …</div>
      ) : (
        <>
          {open.length > 0 ? (
            <div style={{ marginTop: 14 }}>
              <h2 style={{ marginBottom: 10 }}>Laufend</h2>
              <div className="stack">
                {open.map((plan) => (
                  <PlanCard
                    key={plan.uid}
                    plan={plan}
                    onChange={(next) =>
                      setPlans((prev) => prev.map((p) => (p.uid === next.uid ? next : p)))
                    }
                  />
                ))}
              </div>
            </div>
          ) : null}

          {closed.length > 0 ? (
            <div style={{ marginTop: 22 }}>
              <h2 style={{ marginBottom: 10 }}>Abgeschlossen</h2>
              <div className="stack">
                {closed.map((plan) => (
                  <PlanCard
                    key={plan.uid}
                    plan={plan}
                    onChange={(next) =>
                      setPlans((prev) => prev.map((p) => (p.uid === next.uid ? next : p)))
                    }
                  />
                ))}
              </div>
            </div>
          ) : null}

          {plans.length === 0 ? (
            <div className="empty">Noch kein Offboarding durchgeführt.</div>
          ) : null}
        </>
      )}

      {confirmStart && target ? (
        <Confirm
          danger
          title={`Offboarding für ${target.name}?`}
          confirmLabel="Starten"
          message={
            <>
              <p>Damit passiert sofort Folgendes:</p>
              <ul style={{ margin: '0 0 10px', paddingLeft: 20 }}>
                <li>Alle Freigaben dieser Person werden entzogen.</li>
                <li>Das Konto wird gesperrt – Anmeldung nicht mehr möglich.</li>
                <li>Es entsteht eine Checkliste mit allem, was beim Anbieter noch zu tun ist.</li>
              </ul>
              <p className="muted">
                Falls ein Eintrag danach niemanden mehr hätte, der ihn entschlüsseln kann, bricht der
                Vorgang ab und nennt dir die betroffenen Einträge.
              </p>
            </>
          }
          onConfirm={async () => {
            const plan = await api.startOffboarding(target.uid);
            setPlans((prev) => [plan, ...prev.filter((p) => p.uid !== plan.uid)]);
            setStartUid('');
            await reload();
          }}
          onClose={() => setConfirmStart(false)}
        />
      ) : null}
    </>
  );
}
